import React from 'react';

import { useTranslation } from 'react-i18next';

import { useQueryCoinRaces } from '@hooks/api/coin-races';

import { COIN_RACE_STATUS, CoinRaceFiltersState } from '@types';

import { RaceCard } from './race-card';
import * as Styled from './races.styled';

type OwnProps = {
	filtersState: CoinRaceFiltersState;
};

// Determine background gradient based on race duration
const getGradientByDuration = (durationSeconds: number): string => {
	const hours = durationSeconds / 3600;

	if (hours <= 6) {
		// 1h, 3h, 6h – blue (black bottom-left → blue top-right)
		return 'linear-gradient(45deg, rgba(6, 6, 6, 1) 0%, rgba(4, 2, 52, 1) 100%)';
	} else if (hours <= 12) {
		// 12h – purple
		return 'linear-gradient(45deg, rgba(6, 6, 6, 1) 0%, rgba(32, 4, 64, 1) 100%)';
	} else if (hours <= 24) {
		// 24h – orange
		return 'linear-gradient(45deg, rgba(6, 6, 6, 1) 0%, rgba(70, 28, 19, 1) 100%)';
	} else {
		// 1w+ – green
		return 'linear-gradient(45deg, rgba(6, 6, 6, 1) 0%, rgba(23, 60, 49, 1) 100%)';
	}
};

// Determine clock icon color based on race duration
const getClockColorByDuration = (durationSeconds: number): string => {
	const hours = durationSeconds / 3600;

	if (hours <= 6) {
		return '#297AFF'; // 1h, 3h, 6h – blue
	} else if (hours <= 12) {
		return '#AE66FF'; // 12h – purple
	} else if (hours <= 24) {
		return '#FF5E3A'; // 24h – orange
	} else {
		return '#47D7AC'; // 1w – green
	}
};

const Races: React.FC<OwnProps> = ({ filtersState }) => {
	const { t } = useTranslation('coin_race');
	const {
		data: coinRacesData,
		isLoading,
		isError
	} = useQueryCoinRaces({
		status: filtersState.status,
		type: filtersState.selectedType,
		order_end_date: filtersState.sortOrder
	});

	const displayRaces = React.useMemo(() => coinRacesData?.coin_races || [], [coinRacesData?.coin_races]);

	const emptyMessage = React.useMemo(() => {
		if (filtersState.status === COIN_RACE_STATUS.ACTIVE) return 'emptyState.active';
		if (filtersState.status === COIN_RACE_STATUS.PENDING) return 'emptyState.pending';
		if (filtersState.status === COIN_RACE_STATUS.COMPLETED) return 'emptyState.completed';
		return '';
	}, [filtersState.status]);

	if (isLoading) {
		return (
			<Styled.RacesContent>
				<Styled.EmptyMessage>{t('loading_common')}...</Styled.EmptyMessage>
			</Styled.RacesContent>
		);
	}

	if (isError || !coinRacesData) {
		return (
			<Styled.RacesContent>
				<Styled.EmptyMessage>{t('error.races')}</Styled.EmptyMessage>
			</Styled.RacesContent>
		);
	}

	return (
		<Styled.RacesContent>
			{displayRaces.length === 0 ? (
				<Styled.EmptyMessage>{t(emptyMessage)}</Styled.EmptyMessage>
			) : (
				<Styled.RacesList>
					{displayRaces.map(race => {
						const isCompleted = race.status === 'completed';
						const gradient = getGradientByDuration(race.duration_seconds);
						const clockColor = isCompleted
							? 'rgba(255, 255, 255, 0.5)'
							: getClockColorByDuration(race.duration_seconds);

						return <RaceCard key={race.id} race={race} gradient={gradient} clockColor={clockColor} />;
					})}
				</Styled.RacesList>
			)}
		</Styled.RacesContent>
	);
};

export default Races;
